<template>
  <!-- Item not found -->
  <ItemNotFoundState v-if="objectNotFound" :objectName="'Oznámení'">
    <template #backButton>
      <BackButton :target="{ name: 'announcements' }">Oznámení</BackButton>
    </template>
  </ItemNotFoundState>
  <Loading v-else-if="!didLoadData"/>
  <div v-else>
    <!-- Module header -->
    <BackButton :target="{ name: 'announcements' }">Oznámení</BackButton>
    <ModuleHeader>
      <div id="header-title">
        <h2>{{ `${isNew ? 'Přidání' : 'Úprava'} oznámení` }}</h2>
      </div>
      <v-spacer/>
      <SaveButton
        id="save-button"
        class="ml-5"
        :disabled="!isFormValid || !isTextValid || selectedLanguages.length === 0 || ((hasAttribute('events') || hasAttribute('stops')) && !isServiceConfigValid)"
        :is-saving="isSaving"
        @save="saveDetail"/>
    </ModuleHeader>
    <!-- Announcement preview dialog -->
    <v-dialog data-cy="preview-modal" id="preview-modal" v-model="dialog" width="fit-content">
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>Náhled oznámení</span>
          <v-spacer></v-spacer>
          <v-btn id="close-preview-btn" icon="close" variant="flat" @click="() => dialog = false">
          </v-btn>
        </v-card-title>
        <v-card-text>
          <AnnouncementPreview
            id="announcement-preview"
            ref="previewDialog"
            :announcement="announcement"
            :announcementImagePath="previewImagePath"
            :announcementImageIsVideo="announcementImageIsVideo"
            :blockParallel="announcement.blockParallel"
            :getShortText="getShortText"
            :getShortTitle="getShortTitle"
            :initialLanguage="previewLanguage"
            :initialOrientation="previewOrientation"
            :selectedAnnouncementType="selectedAnnouncementType"
            :selectedLanguages="selectedLanguages"
            :textLength="textLength"
            @languageChanged="togglePreviewLanguage"
            @orientationChanged="togglePreviewOrientation"
            inDialog
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Announcement detail -->
    <h4 class="mt-8 mb-5">Obsah oznámení</h4>
    <v-form @submit.prevent ref="form" v-model="isFormValid">
      <div v-bind:class="{ grid: !wrapPreview, 'grid-sm': wrapPreview }">
        <div class="content v-data-table v-table--fixed-header pl-0 pr-2 pt-2 pb-0 ">
          <div class="table v-table__wrapper">
            <table class="table">
              <tbody>
                <tr>
                  <th scope="row" class="table-header">Typ oznámení</th>
                  <td>
                    <AnnouncementTypeSelect
                      :rules="[required_rule]"
                      :announcementTypes="getAnnouncementTypes"
                      :value="announcement.announcementTypeId"
                      v-on:change="changeAnnouncementType"
                    />
                  </td>
                </tr>
                <tr v-if="hasAttribute('stops') && selectedStops.length <= 1">
                  <th scope="row" class="table-header"></th>
                  <td>
                    <v-checkbox
                      id="hide-stop-column"
                      :model-value="hideStopColumn"
                      @update:modelValue="(value) => setHideColumnConfig(value)"
                      label="Nezobrazovat sloupec s názvem zastávky">
                    </v-checkbox>
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="table-header">Jazyk oznámení</th>
                  <td class="d-flex flex-row align-center">
                    <AnnouncementLanguageSelect 
                      :initialValues="selectedLanguages" 
                      v-on:change="(values) => {
                        selectedLanguages = values;
                        onChange();  
                      }"
                    />
                  </td>
                </tr>
                <template v-for="language in selectedLanguages" :key="`title-${language}`">
                  <tr v-if="!hideStopColumn">
                    <th scope="row" class="table-header">Titulek ({{language.toUpperCase()}})</th>
                    <td>
                      <v-text-field
                        :id="`announcement-title-${language}`"
                        :rules="[required_rule]"
                        :maxlength="displaysConstants.announcement.title"
                        v-model="announcement.localizedAttributes[language].title"
                        :label="`${announcement.localizedAttributes[language].title.length}/${displaysConstants.announcement.title} znaků`"
                        @update:model-value="onChange"
                        variant="underlined"
                        color="#78BE13"
                      >
                      </v-text-field>
                    </td>
                  </tr>
                  <tr v-if="!hideStopColumn && hasAttribute('short-title') && announcement.localizedAttributes[language].title.length > displaysConstants.announcement.shortTitle" :key="`s-title-${language}`">
                    <th scope="row" class="table-header">Zkrácený titulek ({{language.toUpperCase()}}) <InfoButton right small>Zkrácená verze titulku je využívána při paralelním zobrazování několika oznámení na jedné obrazovce.</InfoButton></th>
                    <td>
                      <v-text-field
                        :id="`announcement-short-title-${language}`"
                        :rules="[required_rule]" :maxlength="displaysConstants.announcement.shortTitle"
                        v-model="announcement.localizedAttributes[language].shortTitle"
                        :label="`${announcement.localizedAttributes[language].shortTitle ? announcement.localizedAttributes[language].shortTitle.length : 0}/${displaysConstants.announcement.shortTitle} znaků`"
                        @update:model-value="onChange"
                        variant="underlined"
                        color="#78BE13"
                      >
                      </v-text-field>
                    </td>
                  </tr>
                </template>
                <template v-for="language in selectedLanguages" :key="`text-${language}`" >
                  <tr v-if="hasAttribute('text')">
                    <th scope="row" class="table-header">Text ({{language.toUpperCase()}})</th>
                    <td>
                        <Editor
                            :id="`announcement-text-${language}`"
                            v-model="announcement.localizedAttributes[language].text"
                            @keydown="madeUnsavedChanges"
                            api-key="no-api-key"
                            :iniEnt="{
                                plugins: 'lists link image table code help wordcount'
                            }"
                        />
                      <div class="tiptap-validation" v-bind:class="{ 'validation-error': textLength(language) > displaysConstants.announcement.text }">
                        {{`${textLength(language)}/${displaysConstants.announcement.text} znaků ${textLength(language) > displaysConstants.announcement.text && shortTextLength(language) == 0 ? '| Může docházet k chybnému zobrazování, zkraťte text nebo použijte pole pro zkrácený text!' : ''}`}}
                      </div>
                    </td>
                  </tr>
                  <tr v-if="hasAttribute('text') && textLength(language) > displaysConstants.announcement.shortText" :key="`s-text-${language}`">
                    <th scope="row" class="table-header">Zkrácený text ({{language.toUpperCase()}}) <InfoButton right small>Zkrácená verze textu je využívána při paralelním zobrazování několika oznámení na jedné obrazovce.</InfoButton></th>
                    <td>
                        <Editor
                            :id="`announcement-short-text-${language}`"
                            v-model="announcement.localizedAttributes[language].shortText"
                            @keydown="madeUnsavedChanges"
                            api-key="no-api-key"
                            :init="{
                                plugins: 'lists link image table code help wordcount'
                            }"
                        />
                      <div class="tiptap-validation" v-bind:class="{ 'validation-error': shortTextLength(language) > displaysConstants.announcement.shortText || shortTextLength(language) === 0 }">
                        {{`${shortTextLength(language)}/${displaysConstants.announcement.shortText} znaků ${(shortTextErrorLabel(language))}`}}
                      </div>
                    </td>
                  </tr>
                </template>
                  <tr v-if="hasAttribute('events')">
                    <th scope="row" class="table-header">Obsah harmonogramu</th>
                    <td>
                      <SchedulePicker
                        id="announcement-events"
                        v-on:eventsChanged="(output) => {
                          setServiceConfig(output);
                          output.pristine || madeUnsavedChanges();
                          updatePreview();
                        }"
                        :initialValues="inputJSON"
                        :languages="selectedLanguages">
                      </SchedulePicker>
                    </td>
                  </tr>
                <tr v-if="hasAttribute('stops')">
                  <th scope="row" class="table-header">Zobrazované zastávky</th>
                  <td>
                    <StopsPicker
                      v-on:stopsChanged="(output) => {
                        setServiceConfig(output);
                        output.pristine || madeUnsavedChanges();
                        updatePreview();
                      }"
                      :initialValues="inputJSON"
                      :allStops="getStops">
                    </StopsPicker>
                  </td>
                </tr>
                <tr v-if="hasAttribute('facility')">
                  <th scope="row" class="table-header">Výdejna stravy </th>
                  <td>
                    <AnnouncementFacilitySelect
                      id="announcement-facility"
                      :rules="[required_rule]"
                      v-on:facilityChanged="(output) => {
                        setServiceConfig(output);
                        output.pristine || madeUnsavedChanges();
                        updatePreview();
                      }"
                      :initialValues="inputJSON"
                      :allFacilities="getFacilities"
                    />
                  </td>
                </tr>
                <template v-for="language in selectedLanguages" :key="`url-${language}`">
                  <tr v-if="hasAttribute('url')">
                    <th scope="row" class="table-header">Web ({{language.toUpperCase()}}) <InfoButton right small>Ze zadané webové adresy bude automaticky vygenerován QR kód zobrazený v rámci obsahu oznámení.</InfoButton></th>
                    <td>
                      <URLInput 
                        v-bind:id="`announcement-url-${language}`"
                        :url="announcement.localizedAttributes[language].url"
                        v-on:change="(value) => {
                          announcement.localizedAttributes[language].url = value;
                          onChange();
                        }"
                        :maxLength="displaysConstants.announcement.url"
                      />
                    </td>
                  </tr>
                </template>
                <tr v-if="hasAttribute('images')">
                  <th scope="row" id="announcement-media-title" class="table-header">{{`${isFullScreenMediaService ? 'Média' : 'Obrázky'}`}} <InfoButton right small>{{`Nahrajte až ${maxImages} ${isFullScreenMediaService ? 'obrázků nebo videí' : 'obrázků'} o maximální velikosti ${displaysConstants.announcement.image/1000000} MB.`}}</InfoButton></th>
                  <td>
                    <AnnouncementImagePicker 
                      v-on:change="(values) => {
                        changedImages = values;
                        imagesChanged = true;
                        onChange();
                      }"
                      v-on:select="(values) => {
                        changedImages = values;
                        onChange();
                      }"
                      v-on:restore="(values) => {
                        changedImages = pristineImages;
                        imagesChanged = false;
                        onChange();
                      }"
                      :initialImages="pristineImages"
                      :maxImageSize="displaysConstants.announcement.image"
                      :maxImages="maxImages"
                      :imagesChanged="imagesChanged"
                    />
                  </td>
                </tr>
                <!-- Announcement preview for small devices -->
                <tr v-if="selectedAnnouncementType && !dialog && wrapPreview">
                  <th scope="row" class="table-header">Náhled oznámení</th>
                  <td>
                    <AnnouncementPreview
                      id="announcement-preview"
                      ref="preview"
                      :announcement="announcement"
                      :announcementImagePath="previewImagePath"
                      :announcementImageIsVideo="announcementImageIsVideo"
                      :blockParallel="announcement.blockParallel"
                      :getShortText="getShortText"
                      :getShortTitle="getShortTitle"
                      :selectedAnnouncementType="selectedAnnouncementType"
                      :selectedLanguages="selectedLanguages"
                      :textLength="textLength"
                      :initialLanguage="previewLanguage"
                      :initialOrientation="previewOrientation"
                      @zoomIn="zoomIn"
                      @languageChanged="togglePreviewLanguage"
                      @orientationChanged="togglePreviewOrientation"
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="table-header">Režim zobrazení <InfoButton right small>Možnost vynutit zobrazování oznámení vždy na celé ploše informačního panelu, případně stálé viditelnosti.</InfoButton></th>
                  <td :title="serviceBlocksParallel ? 'Zvolený typ oznámení nepodporuje paralelní zobrazení.' : ''">
                    <v-checkbox
                      id="announcement-block-parallel"
                      :disabled="serviceBlocksParallel"
                      v-model="announcement.blockParallel"
                      label="Zakázat paralelní zobrazení"
                      hide-details
                      color="#78BE13"
                    >
                    </v-checkbox>
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="table-header"></th>
                  <td>
                    <v-checkbox
                      id="announcement-is-permanent"
                      v-model="announcement.isPermanent"
                      label="Nastavit jako stále viditelné"
                      class="mb-4"
                      hide-details
                      color="#78BE13"
                    >
                    </v-checkbox>
                  </td>
                </tr>
                <tr>
                  <th scope="row" class="table-header d-flex w-100 pb-4">
                    Minimální doba zobrazení oznámení
                    <InfoButton right small>
                      Nastavuje, jak dlouho bude oznámení zobrazeno na displeji.
                      Při paralelním zobrazení se zohledňují minimální doby zobrazení ostatních oznámení.
                      Pokud není tato hodnota vyplněna, použije se výchozí perioda aktualizace obsahu displeje.
                    </InfoButton>
                  </th>
                  <td>
                    <AnnouncementDuration
                        :initial-duration="announcement.duration"
                        @on-change="args => announcement.duration = args.value"
                    />
                  </td>
                </tr>

                <tr v-if="!announcement.blockParallel && !isPortrait" class="align-center">
                  <th scope="row" class="table-header d-flex w-100 pt-4">Preferovaný formát paralelního zobrazení <InfoButton right small>V rámci paralelního zobrazení více oznámení najednou bude vždy upřednostněn zvolený formát.</InfoButton></th>
                  <td>
                    <AnnouncementPreferredPosition 
                      :position="announcement.preferredPosition"
                      v-on:change="(value) => {
                        announcement.preferredPosition = value;
                        onChange();
                      }"
                    />
                  </td>
                </tr>
                <!-- Displaying scheduler -->
                <tr>
                  <th scope="row" class="table-header">Období zobrazování <InfoButton right small>Datum začátku a konce období, v rámci kterého bude oznámení zobrazováno v dále specifikované dny v týdnu a časy.</InfoButton></th>
                  <td>
                    <v-container class="p-0">
                      <v-row>
                        <v-col class="pl-0">
                          <DatePicker
                            :rules="[required_rule]"
                            title="Od"
                            :clearable="false"
                            v-on:dateChanged="(value) => {
                              announcement.displayFrom = value ? value.unix() : undefined;
                              madeUnsavedChanges();
                            }"
                            :minDate="currentDate"
                            :initialValue="formatDate(announcement.displayFrom)"
                          />
                        </v-col>
                        <v-col>
                          <DatePicker
                            title="Do"
                            :clearable="true"
                            v-on:dateChanged="(value) => {
                              announcement.displayTo = value ? value.add(1,'days').unix() - 1 : undefined;
                              madeUnsavedChanges();
                            }"
                            :minDate="announcement.displayFrom ? formatDate(announcement.displayFrom) : currentDate"
                            :initialValue="!isNew && announcement.displayTo ? formatDate(announcement.displayTo) : undefined"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>           
        </div>
        <!-- Announcement preview for large devices -->
        <AnnouncementPreview
          v-if="selectedAnnouncementType && !dialog && !wrapPreview"
          id="announcement-preview"
          class="preview ml-4" 
          isSmall
          ref="preview"
          :announcement="announcement"
          :selectedAnnouncementType="selectedAnnouncementType"
          :selectedLanguages="selectedLanguages"
          :textLength="textLength"
          :getShortTitle="getShortTitle"
          :getShortText="getShortText"
          :initialLanguage="previewLanguage"
          :initialOrientation="previewOrientation"
          :announcementImagePath="previewImagePath"
          :announcementImageIsVideo="announcementImageIsVideo"
          :blockParallel="announcement.blockParallel"
          @zoomIn="zoomIn"
          @languageChanged="togglePreviewLanguage"
          @orientationChanged="togglePreviewOrientation"
        />
        <div class="displayings v-data-table v-data-table--fixed-header">
          <v-alert
            v-if="displayingsLength <= 0"
            id="announcement-displayings-alert"
            class="mx-4"
            type="warning"
            variant="outlined"
            border="left"
          >
            Bez nastavení časových intervalů pro zobrazování nebude oznámení na informačních panelech viditelné.
          </v-alert>
          <div class="v-data-table__wrapper pt-5 pl-4 pr-4">
            <table class="w-100 ">
              <tbody>
                <tr class="p-0">
                  <td colspan="2">
                    <WeekScheduler
                      :key="`schedule-${displayingsLength}`"
                      v-on:scheduleChanged="(value) => onScheduleChanged(value)"
                      :daysOfWeek="weekDays"
                      :initialValues="announcement.displayings"
                      ref="scheduler">
                    </WeekScheduler>
                  </td>
                </tr>
                <tr>
                  <td class="text-right" colspan="2">
                    <v-btn-toggle class="my-2 no-active" dense>
                      <v-btn 
                        id="announcement-all-week"
                        @click="scheduler.setValue(weekDays.map(day => ({ dayOfWeek: day.index, timeFrom: '00:00', timeTo: '23:59' })))">
                        <span class="hidden-sm-and-down">Celý týden</span>
                      </v-btn>
                      <v-btn id="announcement-delete-displayings" @click="scheduler.setValue([])">
                        <span class="hidden-sm-and-down">Smazat časy</span>
                      </v-btn>
                    </v-btn-toggle>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </v-form>
  </div>
</template>

<script setup>

import {ref, computed, onMounted, onUnmounted, reactive, toRaw} from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import moment from 'moment';
import {
  displaysMaxValues,
  announcementAttributesByType,
  config,
  videoPostfixes,
  videoTypes,
  FOOD_SERVICE,
  FULL_SCREEN_IMAGE_SERVICE,
  PORTRAIT
} from '@/config';
import { removeHTML } from "../../../utils";
import { daysOfWeek } from "./announcements-helpers";
import AnnouncementLanguageSelect from '../../../components/displays/AnnouncementLanguageSelect';
import AnnouncementFacilitySelect from '../../../components/displays/AnnouncementFacilitySelect';
import AnnouncementPreferredPosition from '../../../components/displays/AnnouncementPreferredPosition';
import AnnouncementPreview from '../../../components/displays/AnnouncementPreview';
import AnnouncementTypeSelect from '../../../components/displays/AnnouncementTypeSelect';
import BackButton from "../../../components/layout/BackButton";
import InfoButton from "../../../components/buttons/InfoButton";
import DatePicker from "../../../components/date-picker/DatePicker";
import AnnouncementImagePicker from "../../../components/displays/AnnouncementImagePicker";
import ItemNotFoundState from '../../../components/states/ItemNotFoundState.vue';
import Loading from "../../../components/Loading";
import ModuleHeader from "../../../components/layout/ModuleHeader";
import SaveButton from "../../../components/buttons/SaveButton";
import SchedulePicker from "../../../components/schedule-picker/SchedulePicker";
import StopsPicker from "../../../components/kordis/StopsPicker";
import URLInput from "../../../components/url-input/URLInput";
import WeekScheduler from "@/components/week-scheduler/WeekScheduler.vue";

import { Announcement } from "@/store/modules/displays/Announcement";

import 'tinymce'
import Editor from "@tinymce/tinymce-vue"

/* Required TinyMCE components */
import 'tinymce/icons/default/icons.min.js';
import 'tinymce/themes/silver/theme.min.js';
import 'tinymce/models/dom/model.min.js';

/* Import a skin (can be a custom skin instead of the default) */
import 'tinymce/skins/ui/oxide/skin.js';

/* Import plugins */
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/code';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/table';

/* content UI CSS is required */
import 'tinymce/skins/ui/oxide/content.js';

/* The default content CSS can be changed or replaced with appropriate CSS for the editor content. */
import 'tinymce/skins/content/default/content.js';
import AnnouncementDuration from "@/components/displays/AnnouncementDuration.vue";

const route = useRoute();
const router = useRouter();
const store = useStore();

const cz = 'cz';
const en = 'en';
const emptyAttributes = { title: "", shortTitle: "", text: "<p></p>", shortText: "<p></p>", url: "" };
const isLoading = ref(true);
const isSaving = ref(false);
const announcement = reactive(new Announcement());
const selectedLanguages = ref([cz, en]);
const previewLanguage = ref(cz);
const previewOrientation = ref('blockParallel');
const sectionWidth = ref(1500);
const maxImages = ref(20)
const hideStopColumn = ref(false);
const changedImages = ref([]);
const imagesChanged = ref(false);
const isFormValid = ref(false);
const isServiceConfigValid = ref(false);

const previewDialog = ref(null);
const preview = ref(null);
const dialog = ref(null);
const scheduler = ref(null);

const weekDays = computed(() => daysOfWeek(announcement.displayFrom, announcement.displayTo));

const previewImage = computed(() => changedImages.value?.find(image => image.highlight));

const previewImagePath = computed(() => previewImage.value?.path);

const announcementImageIsVideo = computed(() => isVideoFile(previewImage));

const announcementId = computed(() => {
  const urlId = route.params.announcementId;
  return urlId === "new" ? null : Number(urlId);
});

const serverUrl = computed(() => config.serverUrl);

const isNew = computed(() => announcementId.value === null);

const usesServiceConfig = computed(() => hasAttribute('events') || hasAttribute('stops') || hasAttribute('facility'));

const didLoadData = computed(() => (
    (didLoadAnnouncements.value && didLoadAnnouncementImages || isNew) &&
    didLoadAnnouncementTypes && didLoadStops && didLoadFacilities
  ) && !isLoading.value);

const displaysConstants = computed(() => displaysMaxValues);

const isPortrait = computed(() => selectedAnnouncementType.value ? selectedAnnouncementType.value.orientation === PORTRAIT : false)

const pristineImages = computed(() => isNew.value ? [] : store.getters["announcementImages/getAnnouncementImages"].map((image, index) => ({ ...image, path: `${serverUrl.value}displays/images/${announcementId.value}/${image.id}`, highlight: index === 0})));

const currentDate = computed(() => moment(Date.now()).format('YYYY-MM-DD'));

const isTextValid = computed(() => {
  const overflow = selectedLanguages.value.filter(language => textLength(language) > displaysConstants.value.announcement.shortText);
  if (hasAttribute('text') && overflow.length > 0) {
    return overflow.every(language => shortTextLength(language) > 0);
  }
  if (hasAttribute('text')) return selectedLanguages.value.every(language => textLength(language) > 0);
  return true;
});
const selectedAnnouncementType = computed(() => getAnnouncementTypes.value.find(item => item.id === announcement.announcementTypeId));

const serviceBlocksParallel = computed(() => selectedAnnouncementType.value?.serviceName === FOOD_SERVICE || selectedAnnouncementType.value?.orientation === PORTRAIT);

const attributesByType = computed(() => {
  return announcementAttributesByType[selectedAnnouncementType.value?.serviceName ?? null]});

const isFullScreenMediaService = computed(() => selectedAnnouncementType.value?.serviceName === FULL_SCREEN_IMAGE_SERVICE);

const inputJSON = computed(() => {
  try {
    return JSON.parse(announcement.serviceConfiguration);
  } catch(e) {
    return announcement.serviceConfiguration;
  }
});

const selectedStops = computed(() => inputJSON.value?.stops || []);

const displayingsLength = computed(() => announcement.displayings?.length ?? 0);

const wrapPreview = computed(() => sectionWidth.value < 1100);

onMounted(async () => {
  isLoading.value = true;
  resizeHandler()
  window.addEventListener("resize", resizeHandler);
  await store.dispatch("announcementTypes/fetchAnnouncementTypes", route.params.unitId);
  await store.dispatch("stops/fetchStops");
  await store.dispatch("facilities/fetchFacilities");
  if (!isNew.value) {
    await store.dispatch("announcements/fetchAnnouncementById", announcementId);
    if (objectNotFound.value)
      return;
    await store.dispatch("announcementImages/fetchAnnouncementImagesInfo", announcementId);
    Object.assign(announcement, store.getters['announcements/getAnnouncementById'](announcementId));
    hideStopColumn.value = inputJSON.value?.hideStopColumn;
    const localize = {
      [cz]: !!announcement.localizedAttributes[cz],
      [en]: !!announcement.localizedAttributes[en]
    };
    selectedLanguages.value = selectedLanguages.value.filter(key => !!localize[key]);
    previewLanguage.value = selectedLanguages.value[0];
    changedImages.value = pristineImages.value;
    announcement.localizedAttributes = {
      [cz]: announcement.localizedAttributes[cz] || emptyAttributes,
      [en]: announcement.localizedAttributes[en] || emptyAttributes
    };
  } else {
    await store.dispatch("errors/clear");
  }

  isLoading.value = false;
});

onUnmounted(() => {
  window.removeEventListener("resize", resizeHandler);
});

const didLoadAnnouncements = computed(
  () =>
    store.getters["announcements/didLoadAnnouncements"]
);

const didLoadAnnouncementTypes = computed(
  () =>
    store.getters["announcementTypes/didLoadAnnouncementTypes"]
);

const didLoadAnnouncementImages = computed(
  () =>
    store.getters["announcementImages/didLoadAnnouncementImages"]
);

const didLoadStops = computed(
  () =>
    store.getters["stops/didLoadStops"]
);

const didLoadFacilities = computed(
  () =>
    store.getters["facilities/didLoadFacilities"]
);

const objectNotFound = computed(() => store.getters["errors/objectNotFound"]);

const getFacilities = computed(() => store.getters["facilities/getFacilities"]);

const getStops = computed(() => store.getters["stops/getStops"]);

const getAnnouncementTypes = computed(() => store.getters["announcementTypes/getAnnouncementTypes"]);

const required_rule = (value) => {
  return !!value || 'Povinné'
}

const resizeHandler = () => {
  sectionWidth.value = document.getElementById("auth-section")?.getBoundingClientRect()?.width || 1500   // default value for tests
};

const madeUnsavedChanges = () => {
  store.dispatch("leavePrevent/madeUnsavedChanges");
};

const onScheduleChanged = (schedule) => {
  madeUnsavedChanges();
  announcement.displayings = toRaw(schedule);
}

const isVideoFile = (file) => {
  return videoTypes.includes(file?.type ?? file?.file?.type) || videoPostfixes.includes(file?.postfix);
};

const textLength = (language) => {
  const text = announcement.localizedAttributes[language].text;
  return text && removeHTML(text).length || 0;
};

const getServiceBlocksParallel = (announcementTypeId) => {
  return getAnnouncementTypes.value.find(item => item.id === announcementTypeId)?.serviceName === FOOD_SERVICE;
};

      const shortTextLength = (language) => {
        const shortText = announcement.localizedAttributes[language].shortText;
        return shortText && removeHTML(shortText).length || 0;
      };

      const shortTextErrorLabel = (language) => {
         if (shortTextLength(language) > displaysConstants.value.announcement.shortText) return '| Může docházet k chybnému zobrazování, zkraťte text!';
         if (shortTextLength(language) === 0) return '| Povinné';
         return '';
      };

      const formatDate = (date) => {
        return date ? moment.unix(date).format('YYYY-MM-DD') : undefined;
      };

      const updatePreview = () => {
        preview.value && preview.value.onUpdateContent();
      };

      const onChange = () => {
        madeUnsavedChanges()
        updatePreview();
      };

      const changeAnnouncementType = (id) => {
        announcement.announcementTypeId = id;
        if (!hasAttribute('text')) {
          announcement.text = '';
          announcement.shortText = '';
        }
        if (!hasAttribute('url')) announcement.url = '';
        if (getServiceBlocksParallel(id)) announcement.blockParallel = true;
        if(selectedAnnouncementType.value.orientation === PORTRAIT){
          previewOrientation.value = PORTRAIT
          announcement.blockParallel = true
        }
        onChange();
      };
  
      const setServiceConfig = (output) => {
        isServiceConfigValid.value = output?.valid;
        announcement.serviceConfiguration = { ...output?.value || {}, ...(hasAttribute('stops') ? { hideStopColumn: hideStopColumn.value && output.value?.stops?.length <= 1 } : {}) };
        if (output.value?.stops?.length > 1) { hideStopColumn.value = false; }
      };

      const setHideColumnConfig = (value) => {
        hideStopColumn.value = !hideStopColumn.value
        announcement.serviceConfiguration = { ...(toRaw(announcement.serviceConfiguration) || {}), ...(hasAttribute('stops') ? { hideStopColumn: value } : {})}
        updatePreview();
      };

      const hasAttribute = (attributeName) => attributesByType.value.includes(attributeName);

      const getShortTitle = (language) => announcement.localizedAttributes[language].title.length > displaysConstants.value.announcement.shortTitle
          ? announcement.localizedAttributes[language].shortTitle
          : undefined;

      const getShortText = (language) => textLength(language) > displaysConstants.value.announcement.shortText
          ? announcement.localizedAttributes[language].shortText
          : undefined;

      const saveDetail = async () => {
        if (isFormValid.value && (!hasAttribute('events') || isServiceConfigValid)) {
          isSaving.value = true;
          const localizedAttributes = selectedLanguages.value.reduce((acc, language) => ({
            ...acc,
            [language]: {
              ...announcement.localizedAttributes[language],
              shortTitle: getShortTitle(language),
              shortText: getShortText(language)
            }
          }), {});

          try {
             if (isNew.value) {
              const responseData = await store.dispatch("announcements/createAnnouncement", {
                ...announcement,
                displayings: toRaw(announcement.displayings.map(item => toRaw(item))),
                unitId: route.params.unitId,
                serviceConfiguration: usesServiceConfig.value ? JSON.stringify(announcement.serviceConfiguration) : undefined,
                localizedAttributes
              });

              const promises = [];
              if (imagesChanged.value && hasAttribute('images')) {
                promises.push(store.dispatch("announcementImages/createAnnouncementImages", {
                  id: responseData.id,
                  images: changedImages.value
                }));
              }

              if (displayingsLength.value > 0) {
                promises.push(store.dispatch("announcements/createDisplayings", {
                  announcementId: responseData.id,
                  displayings: toRaw(announcement.displayings.map(item => toRaw(item)))
                }));
              }
              await Promise.all(promises);
              await store.dispatch("leavePrevent/didSaveChanges");
              await router.push({name: "announcements"});
            } else {
              await store.dispatch("announcements/editAnnouncement", {
                ...announcement,
                displayings: toRaw(announcement.displayings.map(item => toRaw(item))),
                unitId: route.params.unitId,
                serviceConfiguration: usesServiceConfig.value ? JSON.stringify(announcement.serviceConfiguration) : undefined,
                localizedAttributes
              });

              const promises = [];
              if (hasAttribute('images') && imagesChanged.value) {
                const imagesToDelete = pristineImages.value
                  .filter(oldImage => !changedImages.value.some(newImage => newImage.id === oldImage.id))
                  .map(image => image.id);
                const imagesToCreate = changedImages.value.filter(image => !image.id);

                if (imagesToDelete.length > 0) {
                  promises.push(store.dispatch("announcementImages/deleteAnnouncementImages", { announcementId: announcementId.value, imagesIds: imagesToDelete }));
                }
                if (imagesToCreate.length > 0) {
                  promises.push(
                    store.dispatch("announcementImages/createAnnouncementImages",{ id: announcementId.value, images: imagesToCreate })
                      .then(() => store.dispatch("announcementImages/fetchAnnouncementImagesInfo",  announcementId ))
                  );
                }
              }
              promises.push(store.dispatch("announcements/deleteDisplayings", announcementId.value));
              if (announcement.displayings) {
                promises.push(store.dispatch("announcements/createDisplayings", {
                  announcementId: announcementId.value,
                  displayings: toRaw(announcement.displayings.map(item => toRaw(item)))
                }));
              }
              await Promise.all(promises);
              changedImages.value = undefined;
              imagesChanged.value = false;
              await store.dispatch("leavePrevent/didSaveChanges");
             }
          } catch (e) {
            console.log(e);
          } finally {
            isSaving.value = false;
          }
        }
      };

      const zoomIn = () => {
        dialog.value = true;
      };

      const togglePreviewLanguage = (language) => {
        previewLanguage.value = language;
        previewDialog.value && previewDialog.value.setPreviewLanguage(language);
      };

      const togglePreviewOrientation = (orientation) => {
        previewOrientation.value = orientation;
        previewDialog.value && previewDialog.value.setPreviewOrientation(orientation);
      };
</script>

<style>
  .grid {
    display: grid;
    grid-template: 
        "content preview"
        "displayings displayings";
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
  }

  .grid-sm {
    display: grid;
    grid-template: 
        "content"
        "displayings";
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
  }

  .content {
    grid-area: content;
    grid-auto-rows: max-content;
  }

  .table {
    width: 100%;
  }

  .preview {
    grid-area: preview;
    grid-auto-rows: min-content;
  }

  .displayings {
    grid-area: displayings
  }

  .table-header {
    font-size: 16px;
    width: 22%;
  }

  .tiptap-vuetify-editor {
    margin-top: 0.5rem;
  }

  .tiptap-validation {
    margin-top: 0.1rem;
    margin-bottom: 0.5rem;
    color: rgba(0,0,0,.6);
    font-size: 12px;
  }

  .validation-error {
    color: rgb(255, 82, 82);
  }

  .tiptap-vuetify-editor div.ProseMirror {
    max-height: 200px;
    overflow: initial;
    margin: 10px !important;
  }

  .tiptap-vuetify-editor__content .ProseMirror p {
    margin: 0px !important;
  }

  div.no-active .v-btn--active::before {
    opacity: 0;
  }
</style>